import { Injectable, ViewContainerRef, inject } from '@angular/core';
import { graphErrorMessage } from '@nuso/common/data-access-api';
import { NotificationService } from '@nuso/common/util-errors';
import { IamAppUserService } from './iam-app-user/iam-app-user.service';
import { DialogSaveEvent, FormDialogService } from '@nuso/forms/ui';
import { FormDefinition } from '@nuso/forms/def-types';
import { AppUser, Group, MediaPoint, PortalUser } from '@nuso/iam/types';
import { catchError, filter, first, switchMap, tap } from 'rxjs';
import { ConfirmService } from '@nuso/common/ui-components';
import { IamGroupMembershipService } from './iam-group-membership/iam-group-membership.service';

import { calculateMembershipType } from '@nuso/iam/util';
import { IamGroupService } from './iam-group/iam-group.service';

interface AddUserToGroupFormData {
  groupId: string;
  member: boolean;
  supervisor: boolean;
}
@Injectable()
export class IamDialogService {
  private iamAppUserService = inject(IamAppUserService);
  private iamGroupService = inject(IamGroupService);
  private iamGroupMembershipService = inject(IamGroupMembershipService);
  private formService = inject(FormDialogService);
  private notificationService = inject(NotificationService);
  private confirmService = inject(ConfirmService);


  attachPortalUser(appUser: AppUser) {
    return this.iamAppUserService.getPortalUsersAvailableForAppUser()
      .pipe(
        first(),
        switchMap((portalUsers: PortalUser[]) => {
          const title = 'Attach Portal User';
          const input = { };
          const formDef: FormDefinition = {
            name: 'iam-attach-portal-user-form',
            fields: [
              {
                name: 'id',
                label: 'Portal User',
                type: 'search-select',
                options: portalUsers.map((t: any) => ({ value: t.id, label: t.name })),
                validators: { required: true },
              }
            ]
          };

          return this.formService
            .createDialog({
              formDef,
              title,
              input,
            })
            .dialogSaving.pipe(
              tap((saving) => saving.busy('Saving...')),
              switchMap((saving) => {
                const portalUser: any = saving.data;
                return this.iamAppUserService
                  .attachPortalUser({ appUserId: appUser.id, portalUserId: +portalUser.id }).pipe(
                    tap(() => saving.complete()),
                    catchError(e => saving.fail(this.notificationService.buildGraphqlErrorMsg(e, 'Unable to attach portal user'))),
                  )
              })
            )
        }),
      )
  }

  detachPortalUser(appUser: AppUser, viewContainerRef: ViewContainerRef) {
    return this.confirmService
      .confirm(
        'Detach portal user?',
        `Are you sure you want to detach portal user ${appUser?.portalUser?.name}?`,
        viewContainerRef,
        true,
        'Detach'
      )
      .pipe(
        first(),
        filter((res: boolean) => res === true),
        switchMap(() =>
          this.iamAppUserService.detachPortalUser(appUser.id)
        )
      )
  }

  detachMediaPointFromAppUser(appUser: AppUser, mediaPoint: MediaPoint, viewContainerRef: ViewContainerRef) {
    return this.confirmService
      .confirm(
        mediaPoint.type === 'USER' ? 'Detach media point?' : 'Remove from media point access?',
        `Are you sure you want to ${mediaPoint.type === 'USER' ? 'detach' : 'remove access to'} media point ${mediaPoint.name}?`,
        viewContainerRef,
        true,
        mediaPoint.type === 'USER' ? 'Detach' : 'Remove Access'
      )
      .pipe(
        first(),
        filter((res: boolean) => res === true),
        switchMap(() =>
          this.iamAppUserService.detachMediaPointFromAppUser(appUser.id, mediaPoint.id)
            .pipe(
              catchError(e => this.notificationService.addGraphqlError(e)),
            )
        )
      );
  }

  detachAppUserFromMediaPoint(appUser: AppUser, mediaPoint: MediaPoint, viewContainerRef: ViewContainerRef) {
    return this.confirmService
      .confirm(
        mediaPoint.type === 'USER' ? 'Detach app user?' : 'Remove app user access?',
        `Are you sure you want to ${mediaPoint.type === 'USER' ? 'detach' : 'remove access of'} app user ${appUser.name}?`,
        viewContainerRef,
        true,
        mediaPoint.type === 'USER' ? 'Detach' : 'Remove Access'
      )
      .pipe(
        first(),
        filter((res: boolean) => res === true),
        switchMap(() =>
          this.iamAppUserService.detachAppUserFromMediaPoint(appUser.id, mediaPoint.id)
            .pipe(
              catchError(e => this.notificationService.addGraphqlError(e)),
            )
        )
      );
  }

  attachMediaPointToAppUser(appUser: AppUser) {
    return this.iamAppUserService.getMediaPointsAvailableForAppUser(appUser.id)
    .pipe(
      first(),
      switchMap(mediaPoints => {
        const title = 'Attach Media Point';
        const input = { };
        const formDef: FormDefinition = {
          name: 'iam-attach-media-point-form',
          fields: [
            {
              name: 'id',
              label: 'Media Point',
              type: 'search-select',
              options: mediaPoints.map((t: any) => ({
                value: t.id,
                label: t.serviceUsername? `${t.serviceUsername} (${t.name})` : t.name })),
              validators: { required: true },
            }
          ]
        };

        return this.formService
          .createDialog<{ id: string }>({
            formDef,
            title,
            input,
          })
          .dialogSaving
      }),
      switchMap((saving: DialogSaveEvent<{ id: string }>) => {
        saving.busy('Saving...');

        const mediaPoint = saving.data;
        return this.iamAppUserService
          .attachMediaPointToAppUser(appUser.id, mediaPoint.id)
          .pipe(
            tap(() => saving.complete()),
            catchError(e => saving.fail(this.notificationService.buildGraphqlErrorMsg(e, 'Unable to attach media point')))
          )
      }),
    )
  }

  addGroupToAppUser(appUser: AppUser) {
    return this.iamGroupService.getGroupsAvailableForAppUser(appUser.id)
      .pipe(
        first(),
        switchMap(results => {
          const { groups } = results;
          const title = 'Add Group';
          const input = { };
          const formDef: FormDefinition = {
            name: 'iam-add-group-form',
            fields: [
              {
                name: 'groupId',
                label: 'Group',
                type: 'search-select',
                options: groups.map((t: any) => ({ value: t.id, label: t.name })),
                validators: { required: true },
              },
              {
                name: 'member',
                label: 'Member',
                type: 'checkbox',
                defaultTo: true,
              },
              ...(appUser.recordingRole !== 'USER' ? [{
                name: 'supervisor',
                label: 'Supervisor',
                type: 'checkbox',
              }] : []),
            ]
          };

          return this.formService
            .createDialog<AddUserToGroupFormData>({
              formDef,
              title,
              input,
            })
            .dialogSaving
        }),
        switchMap((saving: DialogSaveEvent<AddUserToGroupFormData>) => {
          saving.busy('Saving...');

          const { groupId, member, supervisor } = saving.data;
          return this.iamGroupMembershipService
            .addGroupToAppUsers({
              groupId,
              appUserMembers: [{
                appUserId: appUser.id,
                membershipType: calculateMembershipType(member, supervisor),
              }]
            })
            .pipe(
              tap(() => saving.complete()),
              catchError(e => saving.fail(this.notificationService.buildGraphqlErrorMsg(e, 'Unable to add group')))
            )
        }),
      )
  }

  removeUserFromGroup(appUser: AppUser, group: Group, viewContainerRef: ViewContainerRef) {
    return this.confirmService
      .confirm(
        'Delete User?',
        `Are you sure you want to remove user ${appUser.name} from group: ${group.name}?`,
        viewContainerRef,
        true,
        'Delete'
      )
      .pipe(
        first(),
        filter((res: boolean) => res === true),
        switchMap(() =>
          this.iamGroupMembershipService.removeGroupFromAppUsers({
            groupId: group.id,
            appUserId: appUser.id,
          }).pipe(
            catchError(e => this.notificationService.addGraphqlError(e)),
          )
        )
      )
      .subscribe();
  }

}